'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { DM_Mono, DM_Sans } from 'next/font/google'
import CaseStudyCards from '@/app/newlp/CaseStudyCards'
import FaqFooter from '@/components/marketing/FaqFooter'
import WhitelabelCTA from '@/components/services/WhitelabelCTA'
import servicesFaqs from '@/data/services-faqs.json'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'
import {
  SMART_OPS_SEGMENTS,
  SMART_SERVICES_HERO,
  type OpsId,
} from '@/lib/smart-services-data'

const dmSans = DM_Sans({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
})

const dmMono = DM_Mono({
  subsets: ['latin'],
  weight: ['400', '500'],
  variable: '--svc-partner-mono',
})

const SEGMENT_LABEL: Record<OpsId, string> = {
  'marketing-ops': 'Marketing ops',
  'sales-ops': 'Sales ops',
  'data-ops': 'Data ops',
}

const LEGACY_HASH: Record<string, string> = {
  crm: 'sales-ops',
  'crm-stand': 'sales-ops',
  'crm-covers': 'sales-ops',
  'crm-runs': 'get-quote',
  'crm-notfor': 'get-quote',
  'crm-book': 'get-quote',
}

function isOpsId(value: string): value is OpsId {
  return SMART_OPS_SEGMENTS.some((segment) => segment.id === value)
}

function trackCta(text: string, location: string) {
  trackPostHogEvent('cta_click', {
    cta_text: text,
    cta_location: location,
    cta_type: 'secondary',
  })
}

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export default function SmartServicesPage() {
  const [activeOps, setActiveOps] = useState<OpsId>('marketing-ops')

  useEffect(() => {
    function applyHash(scroll: boolean) {
      const raw = window.location.hash.replace('#', '')
      const hash = LEGACY_HASH[raw] ?? raw
      if (isOpsId(hash)) {
        setActiveOps(hash)
        if (scroll) {
          window.setTimeout(() => scrollToId('ops-console'), 80)
        }
        return
      }
      if (scroll && hash) {
        window.setTimeout(() => scrollToId(hash), 80)
      }
    }

    applyHash(true)
    const onHash = () => applyHash(false)
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  function goHref(href: string) {
    if (href.startsWith('#')) {
      scrollToId(href.slice(1))
      return true
    }
    return false
  }

  function selectOps(id: OpsId) {
    setActiveOps(id)
    window.history.replaceState(null, '', `#${id}`)
    trackCta(SEGMENT_LABEL[id], 'smart_ops_tab')
  }

  return (
    <>
      <div className="bg-white lg:px-16">
        <CaseStudyCards
          ctaHref={SMART_SERVICES_HERO.primaryHref}
          bgSrc={SMART_SERVICES_HERO.bgSrc}
          bgOverlay
          badgeLabel=""
          tags={[]}
          heading={SMART_SERVICES_HERO.title}
          headingEm={SMART_SERVICES_HERO.titleEm}
          sub={SMART_SERVICES_HERO.sub}
          ctaLabel={SMART_SERVICES_HERO.primaryCta}
          trustLabel="View client reviews"
          trustHref="#testimonials"
        />
      </div>

      <div className={`smart-services bg-white ${dmSans.className} ${dmMono.variable}`}>
        <noscript>
          <style>{`
            .smart-services .ops-console .ops-panel { display: block !important; }
          `}</style>
        </noscript>

        <section className="band" id="ops-console" style={{ scrollMarginTop: 20 }}>
          <div className="wrap">
            <p className="eyebrow">Three systems, one operator</p>
            <h2 className="h2">
              Pick the system that <em>needs work</em>
            </h2>
            <p className="lead">
              Marketing, sales, and data ops. Same operator across all three, so the handoffs between them are not your problem.
            </p>

            <div className="ops-console">
              <div className="ops-tabs" role="tablist" aria-label="Ops systems">
                {SMART_OPS_SEGMENTS.map((segment) => {
                  const selected = activeOps === segment.id
                  return (
                    <button
                      key={segment.id}
                      type="button"
                      className="ops-tab"
                      role="tab"
                      id={`ops-tab-${segment.id}`}
                      aria-selected={selected}
                      aria-controls={segment.id}
                      tabIndex={selected ? 0 : -1}
                      onClick={() => selectOps(segment.id)}
                    >
                      <span className="n">{segment.n}</span>
                      <span className="t">{segment.label}</span>
                      <span className="s">{segment.tabSub}</span>
                    </button>
                  )
                })}
              </div>

              {SMART_OPS_SEGMENTS.map((segment) => {
                const selected = activeOps === segment.id
                return (
                  <div
                    key={segment.id}
                    className={`ops-panel${selected ? ' is-on' : ''}`}
                    role="tabpanel"
                    id={segment.id}
                    aria-labelledby={`ops-tab-${segment.id}`}
                  >
                    <h3 className="ops-plead">{segment.sentence}</h3>
                    <div className="ops-cols">
                      <div>
                        <p className="tiny">Where you stand</p>
                        <ul className="ops-stand">
                          {segment.stand.map((item) => (
                            <li key={item.n}>
                              <Link
                                href={item.href}
                                onClick={(event) => {
                                  trackCta(item.go, `smart_ops_${segment.id}_stand`)
                                  if (goHref(item.href)) event.preventDefault()
                                }}
                              >
                                <span className="n">{item.n}</span>
                                <span>
                                  <span className="t">{item.title}</span>
                                  <span className="b">{item.body}</span>
                                </span>
                                <span className="go">{item.go} →</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="tiny">What the work covers</p>
                        <ul className="ops-covers">
                          {segment.covers.map((item) => (
                            <li key={item.n}>
                              <div className="ops-cover-head">
                                <span className="t">{item.title}</span>
                                {item.logos?.length ? (
                                  <span className="svc-brand-logos svc-brand-logos--wrap ops-cover-logos">
                                    {item.logos.map((logo) => (
                                      // eslint-disable-next-line @next/next/no-img-element
                                      <img
                                        key={logo.alt}
                                        src={logo.src}
                                        alt={logo.alt}
                                        title={logo.alt}
                                        width={20}
                                        height={20}
                                      />
                                    ))}
                                  </span>
                                ) : null}
                              </div>
                              <p className="b">{item.body}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )
              })}
              <div className={`ops-you svc-partner ${dmMono.variable}`}>
                <div className="svc-partner-inner">
                  <div className="svc-partner-top">
                    <div className="svc-partner-lead">
                      <p className="svc-partner-eyebrow">Is this you?</p>
                      <p className="svc-partner-who">
                        If your systems <b>don&apos;t talk to each other</b>, you can&apos;t see what&apos;s actually making money.
                      </p>
                    </div>
                    <div className="svc-partner-media">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={activeOps === 'marketing-ops' ? '/images/services/hero.jpg' : '/images/crm.png'}
                        alt={
                          activeOps === 'marketing-ops'
                            ? 'Marketing operations: ads, SEO, CRO, and AI search connected through SmartMetrics'
                            : 'Salesforce, HubSpot, Zoho, ActiveCampaign, ClickUp, Pipedrive and HighLevel connected through SmartMetrics'
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <WhitelabelCTA />

      <FaqFooter
        title={
          <>
            Questions clients
            ask first.
          </>
        }
        faqs={servicesFaqs}
      />
    </>
  )
}
